import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, CleaningServices, PendingActions } from '@mui/icons-material';
import { fetchCleaningQueue, markItemClean } from '../api/cleaningQueue';
import type { CleaningQueueItem, CleaningQueueSummary } from '../types/cleaningQueue';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function tallaConUnidad(sizeCm: string | null | undefined): string {
  if (sizeCm == null) return '—';
  const s = String(sizeCm).trim();
  if (s === '') return '—';
  const n = Number(s);
  if (Number.isFinite(n) && !Number.isNaN(n)) return `${s} cm`;
  return s;
}

const DATE_FMT = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function formatDate(iso: string): string {
  try {
    return DATE_FMT.format(new Date(iso));
  } catch {
    return '—';
  }
}

// ─── Metric summary card ──────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: number | null;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

function MetricCard({ label, value, icon, iconBg, iconColor }: MetricCardProps) {
  return (
    <Card
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
          {value === null ? '—' : value}
        </Typography>
      </Box>
    </Card>
  );
}

// ─── Mark-clean confirmation dialog ──────────────────────────────────────────

interface MarkCleanDialogProps {
  item: CleaningQueueItem | null;
  onClose: () => void;
  onConfirmed: (id: number) => void;
}

function MarkCleanDialog({ item, onClose, onConfirmed }: MarkCleanDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!item) {
      setLoading(false);
      setError(null);
    }
  }, [item]);

  const handleConfirm = useCallback(async () => {
    if (!item) return;
    setLoading(true);
    setError(null);
    const result = await markItemClean(item.id);
    setLoading(false);
    if (result.ok) {
      onConfirmed(result.inventoryId);
    } else {
      setError(result.error);
    }
  }, [item, onConfirmed]);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [loading, onClose]);

  const handleErrorDismiss = useCallback(() => setError(null), []);

  return (
    <Dialog open={Boolean(item)} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Marcar como limpio</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={handleErrorDismiss}>
            {error}
          </Alert>
        )}
        <Typography variant="body2">
          ¿Confirmas que{' '}
          <Box component="span" fontWeight={700}>
            {item?.title}
          </Box>{' '}
          ha sido limpiado y está listo para continuar? El ítem saldrá de esta cola.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
          sx={{ bgcolor: '#111827', '&:hover': { bgcolor: '#374151' } }}
        >
          {loading ? 'Guardando…' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Queue row ────────────────────────────────────────────────────────────────

const HEADER_CELL_SX = {
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.6,
  color: 'text.secondary',
  borderBottom: '2px solid',
  borderColor: 'divider',
  py: 1.5,
  bgcolor: '#FAFAFA',
};

interface QueueRowProps {
  item: CleaningQueueItem;
  onMarkClean: (item: CleaningQueueItem) => void;
}

function QueueRow({ item, onMarkClean }: QueueRowProps) {
  const handleClick = useCallback(() => onMarkClean(item), [onMarkClean, item]);

  return (
    <TableRow sx={{ '&:last-child td': { border: 0 } }}>
      {/* Type */}
      <TableCell sx={{ width: 90, pr: 1, py: 1.75 }}>
        <Chip
          label={item.isNew ? 'Nuevo' : 'Usado'}
          size="small"
          sx={{
            bgcolor: item.isNew ? '#F0FDF4' : '#FFF7ED',
            color: item.isNew ? '#15803D' : '#C2410C',
            fontWeight: 700,
            fontSize: 10,
            height: 22,
            letterSpacing: 0.3,
          }}
        />
      </TableCell>

      {/* Product + IDs */}
      <TableCell sx={{ minWidth: 240, py: 1.75 }}>
        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {item.title}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          {item.artId != null ? `UU${item.artId}` : '—'} &middot; #{item.id}
        </Typography>
      </TableCell>

      {/* Size / Consignor */}
      <TableCell sx={{ width: 180, py: 1.75 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {tallaConUnidad(item.sizeCm)}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
          {item.consignorName}
        </Typography>
      </TableCell>

      {/* Received date */}
      <TableCell sx={{ width: 140, py: 1.75 }}>
        <Typography variant="body2" color="text.secondary">
          {formatDate(item.receivedAt)}
        </Typography>
      </TableCell>

      {/* Action */}
      <TableCell align="right" sx={{ width: 160, py: 1.75, pr: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClick}
          startIcon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
          sx={{
            borderColor: '#111827',
            color: '#111827',
            fontWeight: 700,
            fontSize: 11,
            px: 1.5,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: '#111827', color: 'white', borderColor: '#111827' },
          }}
        >
          Marcar limpio
        </Button>
      </TableCell>
    </TableRow>
  );
}

function emptyStateMessage(search: string): string {
  if (search) return 'Intenta con otra búsqueda.';
  return 'Todos los productos usados están limpios. ¡Buen trabajo!';
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminCleaningQueuePage() {
  const [items, setItems] = useState<CleaningQueueItem[]>([]);
  const [summary, setSummary] = useState<CleaningQueueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pendingItem, setPendingItem] = useState<CleaningQueueItem | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadItems = useCallback(
    async (opts?: { search?: string }) => {
      setLoading(true);
      setError(null);
      const result = await fetchCleaningQueue({ search: opts?.search ?? search });
      if (result.ok) {
        setItems(result.data.items);
        setSummary(result.data.summary);
      } else {
        setError(result.error);
      }
      setLoading(false);
    },
    [search]
  );

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(value), 400);
  }, []);

  const handleMarkClean = useCallback((item: CleaningQueueItem) => {
    setPendingItem(item);
  }, []);

  const handleDialogClose = useCallback(() => {
    setPendingItem(null);
  }, []);

  const handleConfirmed = useCallback((confirmedId: number) => {
    setPendingItem(null);
    setItems((prev) => prev.filter((i) => i.id !== confirmedId));
    setSummary((prev) => (prev ? { totalPending: Math.max(0, prev.totalPending - 1) } : prev));
  }, []);

  const mainContent = (() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#111827' }} />
        </Box>
      );
    }
    if (items.length === 0) {
      return (
        <Box
          sx={{
            textAlign: 'center',
            py: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CleaningServices sx={{ fontSize: 64, color: 'grey.300' }} />
          <Typography variant="h6" color="text.secondary">
            No hay ítems pendientes de limpieza
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {emptyStateMessage(search)}
          </Typography>
        </Box>
      );
    }
    return (
      <TableContainer
        component={Paper}
        sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={HEADER_CELL_SX}>Tipo</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Producto</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Talla / Consignador</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Recibido</TableCell>
              <TableCell sx={HEADER_CELL_SX} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((queueItem) => (
              <QueueRow key={queueItem.id} item={queueItem} onMarkClean={handleMarkClean} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  })();

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5, textAlign: 'left' }}>
            Cola de Limpieza
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Productos usados pendientes de limpieza, del más antiguo al más reciente.
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder="Buscar modelo, talla, consignador..."
          value={searchInput}
          onChange={handleSearchInputChange}
          sx={{ width: { xs: '100%', md: 280 }, bgcolor: 'white' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CleaningServices fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary card */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <MetricCard
            label="Ítems pendientes"
            value={summary?.totalPending ?? null}
            icon={<PendingActions />}
            iconBg="#EFF6FF"
            iconColor="#3B82F6"
          />
        </Grid>
      </Grid>

      {/* Content */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {mainContent}

      {/* Confirmation dialog */}
      <MarkCleanDialog
        item={pendingItem}
        onClose={handleDialogClose}
        onConfirmed={handleConfirmed}
      />
    </Box>
  );
}
